// src/views/GrammarQuestView.jsx
// Grammar Word Quest — Modal Overlay interactivo para ejercicios de gramática
import React, { useState, useCallback, useEffect } from 'react';
import {
  X,
  ArrowLeft,
  BookOpen,
  Volume2,
  CheckCircle2,
  RotateCcw,
  ChevronRight,
  Lightbulb,
  Trophy,
  Sparkles,
} from 'lucide-react';
import Mascot from '../components/Mascot';
import WheelSelector from '../components/grammarQuest/WheelSelector';
import WordCollection from '../components/grammarQuest/WordCollection';
import SentenceBuilderChallenge from '../components/grammarQuest/SentenceBuilderChallenge';
import { useGrammarQuest } from '../hooks/useGrammarQuest';
import { CEFR_LEVELS, COLLECTION_CATEGORIES } from '../data/grammarQuestData';


// ─── Selector de Nivel CEFR ───
function LevelSelector({ selectedLevel, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-black hover:bg-slate-50 transition-all active:scale-95 cursor-pointer shadow-sm"
      >
        <span>{selectedLevel}</span>
        <span className="text-slate-400 text-[10px]">▼</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden w-52 animate-in fade-in zoom-in-95 duration-150">
          {CEFR_LEVELS.map(lvl => (
            <button
              key={lvl.id}
              onClick={() => {
                if (lvl.available) { onSelect(lvl.id); setOpen(false); }
              }}
              className={`w-full flex items-center justify-between px-3.5 py-2.5 text-left transition-colors cursor-pointer
                ${lvl.available
                  ? 'hover:bg-slate-50 text-slate-700'
                  : 'opacity-50 cursor-not-allowed text-slate-400'
                }
                ${selectedLevel === lvl.id ? 'bg-blue-50 text-blue-600 font-black' : 'font-semibold'}
              `}
            >
              <span className="text-xs">{lvl.label} — <span className="font-normal text-[11px] text-slate-500">{lvl.description}</span></span>
              {!lvl.available && (
                <span className="text-[10px] font-black bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full">Próx.</span>
              )}
              {lvl.available && selectedLevel === lvl.id && (
                <CheckCircle2 size={14} className="text-blue-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Burbuja de Feedback Adaptativo ───
function FeedbackBubble({ phase, question, selectedOption, activeSkin, onRetry, onNext }) {
  if (phase === 'playing') return null;


  const isCorrect = phase === 'correct';
  const isHint = phase === 'hint';
  const isRevealed = phase === 'revealed';


  return (
    <div className={`rounded-2xl border-2 p-3.5 space-y-2.5 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 ${
      isCorrect  ? 'bg-emerald-50 border-emerald-200' :
      isHint     ? 'bg-amber-50 border-amber-200'     :
                   'bg-rose-50 border-rose-200'
    }`}>
      {/* Mascota + mensaje */}
      <div className="flex items-start gap-2.5">
        <div className="shrink-0 scale-90 -mt-1">
          <Mascot type={activeSkin} />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          {isCorrect && (
            <>
              <div className="flex items-center gap-1.5 mb-0.5">
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                <span className="font-black text-emerald-700 text-xs tracking-wide">¡CORRECTO! ✨</span>
              </div>
              <p className="text-slate-700 font-bold text-xs leading-relaxed">
                <span className="bg-white border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-lg font-black">
                  "{question.collected_word}"
                </span>
                {' '}desbloqueada para tu colección.
              </p>
              <p className="text-emerald-700/90 text-[11px] mt-1">{question.tip}</p>
            </>
          )}
          {isHint && (
            <>
              <div className="flex items-center gap-1.5 mb-0.5">
                <Lightbulb size={15} className="text-amber-500 shrink-0 animate-pulse" />
                <span className="font-black text-amber-700 text-xs tracking-wide">PISTA GRAMATICAL</span>
              </div>
              <p className="text-amber-900 font-semibold text-xs leading-relaxed">{question.tip}</p>
            </>
          )}
          {isRevealed && (
            <>
              <p className="font-black text-slate-800 text-xs mb-0.5">
                Respuesta correcta:{' '}
                <span className="bg-white border border-rose-200 px-1.5 py-0.5 rounded-lg text-rose-700 font-bold">
                  "{question.answer}"
                </span>
              </p>
              <p className="text-slate-600 font-semibold text-[11px] leading-relaxed">{question.tip}</p>
            </>
          )}
        </div>
      </div>


      {/* Categoría desbloqueada */}
      {(isCorrect || isRevealed) && (
        <div className="flex items-center gap-2">
          {(() => {
            const cat = COLLECTION_CATEGORIES[question.category];
            if (!cat) return null;
            return (
              <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${cat.color}`}>
                {cat.emoji} {cat.label}
              </span>
            );
          })()}
        </div>
      )}


      {/* Botones de acción */}
      <div className="flex gap-2 pt-0.5">
        {isHint && (
          <button
            onClick={onRetry}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-amber-500 text-white font-black text-xs border-b-2 border-amber-700 hover:bg-amber-400 transition-all active:scale-95 cursor-pointer shadow-md"
          >
            <RotateCcw size={14} /> Intentar de nuevo
          </button>
        )}
        {(isCorrect || isRevealed) && (
          <button
            onClick={onNext}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs border-b-2 border-blue-800 shadow-md transition-all active:scale-95 cursor-pointer"
          >
            Continuar <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}


// ─── Vista Principal como Modal Overlay ───
export default function GrammarQuestView({ setView, userProgress }) {
  const activeSkin = userProgress?.currentSkin || 'bribark';


  // Bloquear el scroll de fondo mientras el modal esté montado
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);


  const {
    selectedLevel, setSelectedLevel,
    currentQuestion, currentQ, total, score,
    phase, selectedOption, attempts,
    collection, collectedCount, progressPct,
    mascotMessage,
    showCollection, setShowCollection,
    showFinalChallenge, setShowFinalChallenge,
    isLevelCompleted,
    checkAnswer, nextQuestion, retryAfterHint, resetLevel, speak,
  } = useGrammarQuest(activeSkin);


  const [wheelIndex, setWheelIndex] = useState(0);


  // Reset de rueda al cambiar pregunta
  useEffect(() => {
    setWheelIndex(0);
  }, [currentQ]);


  const handleCheck = useCallback(() => {
    if (!currentQuestion || phase !== 'playing') return;
    checkAnswer(currentQuestion.options[wheelIndex]);
  }, [currentQuestion, phase, wheelIndex, checkAnswer]);


  // Palabra que se muestra en el hueco y que debe leer el audio
  const getSpokenWord = () => {
    if (!currentQuestion) return '';
    if (phase === 'playing') return currentQuestion.options[wheelIndex] || '';
    if (phase === 'hint') return selectedOption || currentQuestion.options[wheelIndex] || '';
    return currentQuestion.answer; // correct / revealed
  };


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-0 sm:p-3 animate-in fade-in duration-300"
      style={{ overscrollBehavior: 'contain' }}
    >
      {/* Contenedor Modal */}
      <div
        className="w-full max-w-md h-full sm:h-[94vh] sm:max-h-[850px] sm:rounded-[2.5rem] bg-gradient-to-b from-white via-blue-50/40 to-blue-50 text-slate-800 flex flex-col overflow-hidden shadow-2xl relative border-0 sm:border sm:border-blue-100"
      >
        {/* ── HEADER DEL MODAL ── */}
        <header className="shrink-0 px-4 pt-3.5 pb-3 border-b border-blue-100 bg-white/80 backdrop-blur">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setView('lobby')}
              className="p-2 rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-all active:scale-90 cursor-pointer shadow-sm"
              aria-label="Cerrar desafío gramatical"
            >
              <X size={18} />
            </button>


            <div className="flex flex-col items-center">
              <span className="text-blue-600 text-[10px] font-black uppercase tracking-widest">
                Grammar Word Quest
              </span>
              <h1 className="text-slate-800 font-black text-base leading-tight">
                Master Sentence Builder
              </h1>
            </div>


            <LevelSelector
              selectedLevel={selectedLevel}
              onSelect={(lvl) => { setSelectedLevel(lvl); }}
            />
          </div>


          {/* Barra de progreso */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-[11px] font-black text-slate-500">
              <span>
                {phase !== 'finished'
                  ? `Pregunta ${Math.min(currentQ + 1, total)} de ${total}`
                  : `¡Nivel completado! 🏆`
                }
              </span>
              <span className="flex items-center gap-1 text-blue-600">
                <BookOpen size={11} />
                {collectedCount}/{total} palabras
              </span>
            </div>
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </header>


        {/* ── CUERPO DEL MODAL ── */}
        {phase === 'finished' && !showFinalChallenge ? (
          // Pantalla de nivel completado
          <div className="flex-1 overflow-y-auto px-5 py-6 flex flex-col items-center justify-center text-center space-y-4">
            <Trophy size={56} className="text-amber-500 drop-shadow-md animate-bounce" />
            <div>
              <p className="text-blue-600 text-[11px] font-black uppercase tracking-widest mb-1">
                Grammar Word Quest · {selectedLevel}
              </p>
              <h2 className="text-slate-800 font-black text-2xl">¡Nivel Completado!</h2>
              <p className="text-slate-500 text-xs font-semibold mt-1">
                {score}/{total} respuestas correctas · {collectedCount} palabras en tu colección
              </p>
            </div>
            <div className="scale-90 my-1">
              <Mascot type={activeSkin} message="¡Lo lograste! 🏆" />
            </div>
            <div className="flex flex-col gap-2.5 w-full max-w-xs pt-1">
              <button
                onClick={() => setShowFinalChallenge(true)}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 font-black text-sm border-b-4 border-amber-600 shadow-xl hover:from-amber-300 transition-all active:scale-95 active:border-b-0 cursor-pointer"
              >
                🏆 Reto Final de Frases
              </button>
              <button
                onClick={() => setShowCollection(true)}
                className="w-full py-3 rounded-2xl bg-white border-2 border-blue-100 text-blue-700 font-bold text-xs hover:border-blue-300 transition-all active:scale-95 cursor-pointer shadow-sm"
              >
                📖 Ver mi colección ({collectedCount}/{total})
              </button>
              <button
                onClick={resetLevel}
                className="w-full py-2.5 rounded-2xl bg-slate-50 border border-slate-200 text-slate-500 font-bold text-xs hover:bg-slate-100 transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <RotateCcw size={13} /> Repetir nivel
              </button>
              <button
                onClick={() => setView('lobby')}
                className="flex items-center justify-center gap-1.5 text-slate-500 font-bold text-xs hover:text-blue-600 transition-colors cursor-pointer pt-1"
              >
                <ArrowLeft size={14} /> Volver al lobby
              </button>
            </div>
          </div>
        ) : (
          // Vista activa de ejercicios
          <main className="flex-1 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain flex flex-col gap-4">
            {/* Puntuación + Colección */}
            <div className="flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 shadow-sm">
                <Sparkles size={13} className="text-amber-500" />
                <span className="text-amber-700 font-black text-xs">{score} pts</span>
              </div>
              <button
                onClick={() => setShowCollection(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-blue-100 text-blue-700 text-xs font-bold hover:border-blue-300 transition-all active:scale-95 cursor-pointer shadow-sm"
              >
                <BookOpen size={13} className="text-blue-600" />
                Mi Colección
              </button>
            </div>


            {/* Mensaje de Mascota Flotante */}
            {mascotMessage && (
              <div className="bg-white border border-blue-100 rounded-xl px-3 py-2 flex items-center gap-2.5 animate-in fade-in slide-in-from-top-1 duration-200 shadow-sm shrink-0">
                <span className="text-xl shrink-0">
                  {mascotMessage.includes('Correcta') || mascotMessage.includes('Perfecto') || mascotMessage.includes('bien') ? '🎉' :
                   mascotMessage.includes('Pista') || mascotMessage.includes('💡') ? '💡' :
                   mascotMessage.includes('respuesta') ? '📝' : '🌟'}
                </span>
                <p className="text-slate-700 font-semibold text-xs leading-relaxed">{mascotMessage}</p>
              </div>
            )}


            {currentQuestion && (
              <>
                {/* ── TARJETA DE PREGUNTA ── */}
                <div className="rounded-3xl p-4 space-y-3 bg-white border-2 border-blue-100 shadow-md shrink-0">
                  {/* Badge gramatical */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                      {currentQuestion.grammar}
                    </span>
                  </div>


                  {/* Oración con hueco */}
                  <p className="text-slate-800 font-bold text-xl leading-snug">
                    {currentQuestion.sentence.split('___').map((part, i, arr) => (
                      <React.Fragment key={i}>
                        {part}
                        {i < arr.length - 1 && (
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-lg border-2 font-black text-base mx-1 transition-all duration-300 ${
                              phase === 'correct'  ? 'bg-emerald-50 border-emerald-400 text-emerald-700 ring-2 ring-emerald-200' :
                              phase === 'revealed' ? 'bg-amber-50 border-amber-400 text-amber-700' :
                              phase === 'hint'     ? 'bg-amber-50 border-amber-300 text-amber-700 animate-pulse' :
                                                     'bg-blue-50 border-blue-300 border-dashed text-blue-700'
                            }`}
                          >
                            {getSpokenWord() || '___'}
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </p>


                  {/* Traducción + TTS */}
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    <p className="text-slate-500 text-xs font-medium leading-relaxed flex-1 italic">
                      "{currentQuestion.translation}"
                    </p>
                    <button
                      onClick={() => speak(currentQuestion.sentence.replace('___', getSpokenWord()))}
                      className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50 border border-blue-100 text-blue-600 hover:bg-blue-100 transition-all active:scale-90 cursor-pointer"
                      aria-label="Escuchar oración"
                    >
                      <Volume2 size={16} />
                    </button>
                  </div>
                </div>


                {/* ── SELECTOR RUEDA 3D ── */}
                {phase === 'playing' && (
                  <div className="space-y-3 shrink-0">
                    <WheelSelector
                      options={currentQuestion.options}
                      selectedIndex={wheelIndex}
                      onChange={setWheelIndex}
                      disabled={phase !== 'playing'}
                    />


                    {/* Botón COMPROBAR */}
                    <button
                      onClick={handleCheck}
                      className="w-full py-3.5 rounded-2xl font-black text-sm border-b-4 transition-all active:scale-95 active:border-b-0 shadow-lg bg-blue-600 hover:bg-blue-500 border-blue-800 text-white shadow-blue-200 cursor-pointer"
                    >
                      ✓ COMPROBAR
                    </button>
                  </div>
                )}


                {/* ── FEEDBACK ADAPTATIVO ── */}
                <FeedbackBubble
                  phase={phase}
                  question={currentQuestion}
                  selectedOption={selectedOption}
                  activeSkin={activeSkin}
                  onRetry={retryAfterHint}
                  onNext={nextQuestion}
                />
              </>
            )}
          </main>
        )}


        {/* ── DRAWER DE COLECCIÓN DE PALABRAS ── */}
        {showCollection && (
          <WordCollection
            collection={collection}
            onClose={() => setShowCollection(false)}
            totalQuestions={total}
          />
        )}


        {/* ── RETO FINAL SENTENCE BUILDER ── */}
        {showFinalChallenge && (
          <SentenceBuilderChallenge
            collection={collection}
            onClose={() => setShowFinalChallenge(false)}
            onReset={resetLevel}
            activeSkin={activeSkin}
            level={selectedLevel}
          />
        )}
      </div>
    </div>
  );
}